import { StatSectionCard } from "./stats/StatSectionCard";
import { ProgressBar } from "./stats/ProgressBar";
import { Accordion } from "./stats/Accordion";
import { SuggestionCard } from "./stats/SuggestionCard";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../translations/LanguageContext";

// ----- COLOURS -----
const C = [
    "#4f8ef7", // blue
    "#f7924f", // orange
    "#4fcf8e", // green
    "#f74f6e", // red
    "#a64ff7", // purple
    "#f7d44f", // yellow
    "#4fd4f7", // cyan
    "#f74fca", // pink
];

// CEFR levels in order, used for the linear scale
const CEFR_LEVELS = ["A1", "A2", "B1", "B2"];

// Links to official CEFR descriptor pages
const CEFR_LINKS = {
    A1: "https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions",
    A2: "https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions",
    B1: "https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions",
    B2: "https://www.coe.int/en/web/common-european-framework-reference-languages/level-descriptions",
};

// ----- AVERAGE / EXPECTED VALUES -----
// Placeholder benchmark values. All set to 0 until real reference data is
// available. Swap these for real numbers (or fetch them from the API)
// once they exist
const AVERAGES = {
    readability: 0,
    wordCount: 0,
    sentenceCount: 0,
    avgSentenceLength: 0,
    ttr: 0,
    a1Coverage: 0,
    a2Coverage: 0,
    b1Coverage: 0,
    basicVocab: 0,
    intermediateVocab: 0,
    stopWords: 0,
    uniqueStopWords: 0,
    a1Matches: 0,
    a2Matches: 0,
    b1Matches: 0,
    totalMutations: 0,
    softMutations: 0,
    nasalMutations: 0,
    aspirateMutations: 0,
    hProthesis: 0,
    verbNouns: 0,
    avgHeadDistance: 0,
    avgTreeDepth: 0,
    maxTreeDepth: 0,
    avgBranchingFactor: 0,
    subjectRatio: 0,
    leftRightBranchingRatio: 0,
    clausesPerSentence: 0,
    totalClauses: 0,
    auxiliaryVerbs: 0,
};

// ----- HELPERS -----

// Returns a red/yellow/green colour based on the user value
function getColour(enrichedValue, fallback = C[0]) {
    if (!enrichedValue || enrichedValue.user === null) return fallback;
    const pct = enrichedValue.user;
    if (pct <= 33) return "#4fcf8e";
    if (pct <= 66) return "#f7d44f";
    return "#f74f6e";
}

// Converts a ratio (0–1) to a rounded percentage
function toPercent(ratio) {
    if (ratio === null || ratio === undefined) return 0;
    return Math.round(ratio * 100);
}

// Formats a number to a given number of decimal places
function fmt(value, decimals = 2) {
    if (value === null || value === undefined) return "-";
    return typeof value === "number" ? value.toFixed(decimals) : value;
}

// Small inline info tooltip component
function InfoTip({ text }) {
    const iconRef = useRef(null);
    const [coords, setCoords] = useState(null); // null = hidden

    function show() {
        const rect = iconRef.current.getBoundingClientRect();
        setCoords({
            top: rect.top,
            left: rect.left + rect.width / 2,
        });
    }

    return (
        <span
            className="info-tip-wrap"
            onMouseEnter={show}
            onMouseLeave={() => setCoords(null)}
        >
            <img
                ref={iconRef}
                src="/icons/help.png"
                alt="info"
                className="info-tip-icon"
            />
            {coords && createPortal(
                <span
                    className="info-tip-tooltip info-tip-tooltip--portal"
                    style={{ top: coords.top, left: coords.left }}
                >
                    {text}
                </span>,
                document.body
            )}
        </span>
    );
}

// CEFR Indicator - full component that contains all details about the CEFR score
function CEFRIndicator({ label, confidence, size = "full", showDescription = true, showLink = true }) {
    const { t } = useTranslation();
    const isCompact = size === "compact";
    const idx = CEFR_LEVELS.indexOf(label);
    const badgePos = idx >= 0 ? ((idx + 0.5) / CEFR_LEVELS.length) * 100 : null;
    const descriptions = t("resultsBox.cefr.descriptions");

    return (
        <div className={isCompact ? "cefr-mini" : "cefr-hero"}>
            <div className={isCompact ? "cefr-mini-top" : "cefr-hero-top"}>
                <span className={isCompact ? "cefr-mini-label" : "cefr-hero-label"}>
                    {isCompact ? t("resultsBox.cefr.miniLabel") : t("resultsBox.cefr.heading")}
                    {!isCompact && (
                        <InfoTip text={t("resultsBox.cefr.infoTip")} />
                    )}
                </span>
                <span className={`confidence-badge ${isCompact ? "confidence-badge--mini" : ""}`}>
                    {t("resultsBox.cefr.confidence", { percent: Math.round((confidence ?? 0) * 100) })}
                </span>
            </div>

            {!isCompact && <div className="cefr-hero-level">{label ?? "—"}</div>}

            {!isCompact && showDescription && label && (
                <p className="cefr-hero-desc">{descriptions[label]}</p>
            )}

            <div className="cefr-scale-wrap">
                <CEFRScale label={label} size={size} />
                {isCompact && label && (
                    <span className="cefr-mini-badge" style={{ left: `${badgePos}%` }}>{label}</span>
                )}
            </div>

            {!isCompact && showLink && label && CEFR_LINKS[label] && (
                <a className="cefr-link" href={CEFR_LINKS[label]} target="_blank" rel="noreferrer">
                    {t("resultsBox.cefr.link")}
                </a>
            )}
        </div>
    );
}

// CEFR linear scale — highlights the current level among A1 - C2
function CEFRScale({ label, size = "full" }) {
    const idx = CEFR_LEVELS.indexOf(label);
    const fillPercent = idx >= 0 ? ((idx + 0.5) / CEFR_LEVELS.length) * 100 : 0;

    return (
        <div className={`cefr-scale ${size === "compact" ? "cefr-scale--compact" : ""}`}>
            <div className="cefr-scale-track" />
            <div className="cefr-scale-fill" style={{ width: `${fillPercent}%` }} />
            {CEFR_LEVELS.map((level, i) => (
                <div
                    key={level}
                    className={`cefr-scale-step ${i === idx ? "cefr-scale-step--active" : ""} ${i < idx ? "cefr-scale-step--passed" : ""}`}
                >
                    <div className="cefr-scale-dot" />
                    <span className="cefr-scale-label">{level}</span>
                </div>
            ))}
        </div>
    );
}

// Generates simple plain-English (or plain-Welsh) suggestions based on
// analysis results. Templates live in resultsBox.suggestions.items.
function generateSuggestions(data, t) {
    const suggestions = [];
    const r = data?.results;
    const e = data?.enriched_results;
    if (!r || !e) return suggestions;

    const items = t("resultsBox.suggestions.items");

    const ttr = e["TTR (Type Token Ratio)"]?.user;
    if (ttr !== null && ttr < 0.4) {
        suggestions.push(items.repetitiveVocab);
    }

    const avgSentLen = e["Average Sentence Length"]?.user;
    if (avgSentLen !== null && avgSentLen > 25) {
        suggestions.push(items.longSentences);
    }

    const softMuts = r["Number of Soft mutations"];
    if (softMuts !== null && softMuts === 0) {
        suggestions.push(items.noSoftMutations);
    }

    const clausesPerSent = r["Clause count per sentence"];
    if (clausesPerSent !== null && clausesPerSent < 1.2) {
        suggestions.push(items.simpleStructures);
    }

    const b1Ratio = r["b1 - ratio to all words"];
    if (b1Ratio !== null && b1Ratio < 0.05) {
        suggestions.push(items.lowB1Coverage);
    }

    return suggestions.slice(0, 4);
}

// ----- COMPONENT -----
export function ResultsBox({ data, isOutdated }) {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("overview");
    const [showAverages, setShowAverages] = useState(false);
    const [openAccordion, setOpenAccordion] = useState(null);

    if (!data) return (
        <article className="results-box">
            <div className="input-top input-section">
                <p>{t("resultsBox.analysisResult")}</p>
            </div>
            <p className="results-empty">{t("resultsBox.empty")}</p>
        </article>
    );

    const r = data.results;
    const e = data.enriched_results;
    const preds = data.preds;

    const tabsDict = t("resultsBox.tabs");
    const tabs = [
        ["overview", tabsDict.overview],
        ["proficiency", tabsDict.proficiency],
        ["vocabulary", tabsDict.vocabulary],
        ["grammar", tabsDict.grammar],
        ["structure", tabsDict.structure],
        ["suggestions", tabsDict.suggestions],
    ];

    const rb = {
        overview: t("resultsBox.overview"),
        proficiency: t("resultsBox.proficiency"),
        vocabulary: t("resultsBox.vocabulary"),
        grammar: t("resultsBox.grammar"),
        structure: t("resultsBox.structure"),
        suggestions: t("resultsBox.suggestions"),
    };

    // Appends the average/expected value onto a card label when the toggle is on.
    // Falls back to the plain label otherwise, so StatSectionCard needs no changes.
    function withAvg(label, key) {
        return showAverages ? `${label} • Avg: ${fmt(AVERAGES[key])}` : label;
    }

    // Small inline note used next to plain occurrence-row values.
    function AvgNote({ value }) {
        if (!showAverages) return null;
        return <span className="occurrence-average"> (avg: {fmt(value)})</span>;
    }

    return (
        <article className="results-box">

            {isOutdated && (
                <div className="analysis-warning">
                    {t("resultsBox.outdatedWarning")}
                </div>
            )}

            {/* Tab bar */}
            <div className="button-wrap">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        className={`toggle-btn ${activeTab === key ? "toggle-btn--active" : ""}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* ── Overview ── */}
            {activeTab === "overview" && (
                <section className="tab-section">

                    {/* CEFR info*/}
                    <CEFRIndicator label={preds?.cefr_label} confidence={preds?.cefr_confidence} />

                    {/* Secondary stats — grouped into two labelled sections so the
                        Overview tab matches the pattern used elsewhere in the box */}
                    <p className="section-label">{rb.overview.readabilitySection}</p>
                    <div className="stat-gallery">
                        <StatSectionCard
                            name={rb.overview.gulpeaseName}
                            value={fmt(e["Adapted Gulpease readability 1"]?.user)}
                            label={withAvg(rb.overview.gulpeaseLabel, "readability")}
                        />
                        <StatSectionCard
                            name={rb.overview.avgSentenceLengthName}
                            value={fmt(e["Average Sentence Length"]?.user)}
                            label={withAvg(rb.overview.avgSentenceLengthLabel, "avgSentenceLength")}
                            colour={getColour(e["Average Sentence Length"])}
                        />
                    </div>

                    <p className="section-label">{rb.overview.textSizeSection}</p>
                    <div className="stat-gallery">
                        <StatSectionCard
                            name={rb.overview.totalWordsName}
                            value={r["Word Count"]}
                            label={withAvg(rb.overview.totalWordsLabel, "wordCount")}
                        />
                        <StatSectionCard
                            name={rb.overview.sentencesName}
                            value={r["Sentence Count"]}
                            label={withAvg(rb.overview.sentencesLabel, "sentenceCount")}
                        />
                    </div>
                </section>
            )}

            {/* ── Proficiency ── */}
            {
                activeTab === "proficiency" && (
                    <section className="tab-section">
                        {/* CEFR container*/}
                        <CEFRIndicator label={preds?.cefr_label} confidence={preds?.cefr_confidence} />

                        <p className="section-label">
                            {rb.proficiency.coverageSection}
                            <InfoTip text={rb.proficiency.coverageInfoTip} />
                        </p>
                        <div className="progress-stack">
                            <ProgressBar label={rb.proficiency.levels.a1} value={toPercent(r["a1 - ratio to all words"])}
                                         colour={C[0]} />
                            <ProgressBar label={rb.proficiency.levels.a2} value={toPercent(r["a2 - ratio to all words"])}
                                         colour={C[1]} />
                            <ProgressBar label={rb.proficiency.levels.b1} value={toPercent(r["b1 - ratio to all words"])}
                                         colour={C[2]} />
                            <ProgressBar
                                label={rb.proficiency.levels.other}
                                value={100 - toPercent(r["a1 - ratio to all words"]) - toPercent(r["a2 - ratio to all words"]) - toPercent(r["b1 - ratio to all words"])}
                                colour="#aaa"
                            />
                        </div>
                        <div className="stat-gallery">
                            <StatSectionCard name={rb.proficiency.basicVocabName}
                                             value={`${toPercent(r["a1 - ratio to all words"]) + toPercent(r["a2 - ratio to all words"])}%`}
                                             label={withAvg(rb.proficiency.basicVocabLabel, "basicVocab")} colour={C[0]} />
                            <StatSectionCard name={rb.proficiency.intermediateName}
                                             value={`${toPercent(r["b1 - ratio to all words"])}%`}
                                             label={withAvg(rb.proficiency.intermediateLabel, "intermediateVocab")} colour={C[1]} />
                        </div>
                    </section>
                )
            }

            {/* ── Vocabulary ── */}
            {
                activeTab === "vocabulary" && (
                    <section className="tab-section">

                        {/* Vocabulary diversity first, TTR below with explanation */}
                        <div className="stat-gallery" style={{ marginBottom: "0.5rem" }}>
                            <StatSectionCard
                                name={rb.vocabulary.diversityName}
                                value={fmt(e["TTR (Type Token Ratio)"]?.user)}
                                label={withAvg(rb.vocabulary.diversityLabel, "ttr")}
                                colour={getColour(e["TTR (Type Token Ratio)"])}
                            />
                        </div>
                        <p className="section-note">
                            {rb.vocabulary.note}
                            <InfoTip text={rb.vocabulary.noteInfoTip} />
                        </p>

                        <Accordion
                            title={rb.vocabulary.cefrAccordion.title}
                            open={openAccordion === "cefr"}
                            onToggle={() =>
                                setOpenAccordion(current =>
                                    current === "cefr" ? null : "cefr"
                                )
                            }
                        >
                            <div className="progress-stack">
                                <ProgressBar label="A1" value={toPercent(r["a1 - ratio to all words"])} colour={C[0]} />
                                <ProgressBar label="A2" value={toPercent(r["a2 - ratio to all words"])} colour={C[1]} />
                                <ProgressBar label="B1" value={toPercent(r["b1 - ratio to all words"])} colour={C[2]} />
                            </div>
                            <div className="stat-gallery" style={{ marginTop: "1rem" }}>
                                <StatSectionCard name={rb.vocabulary.cefrAccordion.a1Matches} value={r["a1 - unique matches"]}
                                                 label={withAvg(rb.vocabulary.cefrAccordion.matchesLabel, "a1Matches")}
                                                 colour={C[0]} />
                                <StatSectionCard name={rb.vocabulary.cefrAccordion.a2Matches} value={r["a2 - unique matches"]}
                                                 label={withAvg(rb.vocabulary.cefrAccordion.matchesLabel, "a2Matches")}
                                                 colour={C[1]} />
                                <StatSectionCard name={rb.vocabulary.cefrAccordion.b1Matches} value={r["b1 - unique matches"]}
                                                 label={withAvg(rb.vocabulary.cefrAccordion.matchesLabel, "b1Matches")}
                                                 colour={C[2]} />
                            </div>
                        </Accordion>

                        <Accordion
                            title={rb.vocabulary.frequencyAccordion.title}
                            open={openAccordion === "frequency"}
                            onToggle={() =>
                                setOpenAccordion(current =>
                                    current === "frequency" ? null : "frequency"
                                )
                            }
                        >
                            <p className="section-note" style={{ marginBottom: "0.75rem" }}>
                                {rb.vocabulary.frequencyAccordion.note}
                            </p>
                            <div className="progress-stack">
                                {["K1", "K2", "K3", "K4", "K5", "K6+"].map((k, i) => (
                                    <ProgressBar key={k} label={k} value={toPercent(r[`${k} - ratio to all words`])}
                                                 colour={C[i]} />
                                ))}
                            </div>
                        </Accordion>

                        <Accordion
                            title={rb.vocabulary.stopwordsAccordion.title}
                            open={openAccordion === "stopwords"}
                            onToggle={() =>
                                setOpenAccordion(current =>
                                    current === "stopwords" ? null : "stopwords"
                                )
                            }
                        >
                            <p className="section-note" style={{ marginBottom: "0.75rem" }}>
                                {rb.vocabulary.stopwordsAccordion.note}
                            </p>
                            <div className="stat-gallery">
                                <StatSectionCard name={rb.vocabulary.stopwordsAccordion.functionWordsName}
                                                 value={`${toPercent(r["cy_ataleiriau - ratio to all words"])}%`}
                                                 label={withAvg(rb.vocabulary.stopwordsAccordion.functionWordsLabel, "stopWords")} colour={C[0]} />
                                <StatSectionCard name={rb.vocabulary.stopwordsAccordion.uniqueFunctionWordsName} value={r["cy_ataleiriau - unique stopwords"]}
                                                 label={withAvg(rb.vocabulary.stopwordsAccordion.uniqueFunctionWordsLabel, "uniqueStopWords")} colour={C[1]} />
                            </div>
                        </Accordion>
                    </section>
                )
            }

            {/* ── Grammar ── */}
            {
                activeTab === "grammar" && (
                    <section className="tab-section">
                        <p className="section-label">{rb.grammar.posSection}</p>
                        <div className="progress-stack">
                            <ProgressBar label={rb.grammar.pos.verbs} value={r["Number of verbs"]} max={r["Word Count"]} colour={C[0]} />
                            <ProgressBar label={rb.grammar.pos.nouns} value={r["Number of Nouns"]} max={r["Word Count"]} colour={C[1]} />
                            <ProgressBar label={rb.grammar.pos.adverbs} value={r["Number of adverbs"]} max={r["Word Count"]}
                                         colour={C[2]} />
                            <ProgressBar label={rb.grammar.pos.adjectives} value={r["Number of adjectives"]} max={r["Word Count"]}
                                         colour={C[3]} />
                            <ProgressBar label={rb.grammar.pos.conjunctions} value={r["Number of conjunctions"]} max={r["Word Count"]}
                                         colour={C[4]} />
                            <ProgressBar label={rb.grammar.pos.particles} value={r["Number of particles"]} max={r["Word Count"]}
                                         colour={C[5]} />
                            <ProgressBar label={rb.grammar.pos.articles} value={r["Number of articles"]} max={r["Word Count"]}
                                         colour={C[6]} />
                            <ProgressBar label={rb.grammar.pos.prepositions} value={r["Number of prepositions"]} max={r["Word Count"]}
                                         colour={C[7]} />
                            <ProgressBar label={rb.grammar.pos.pronouns} value={r["Number of pronouns"]} max={r["Word Count"]}
                                         colour={C[0]} />
                        </div>

                        <p className="section-label">{rb.grammar.verbFormsSection}</p>
                        <div className="occurrence-list">
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.pluperfect}</span><span>{r["Number of verbs in Pluperfect tense"]}</span></div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.imperfect}</span><span>{r["Number of verbs in Imperfect tense"]}</span></div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.impersonal}</span><span>{r["Number of verbs in Impersonal tense"]}</span></div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.subjunctive}</span><span>{r["Number of verbs in Subjunctive tense"]}</span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.conditional}</span><span>{r["Number of verbs in Conditional tense"]}</span></div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.verbForms.verbNouns}</span>
                                <span>{r["Number of verb nouns"]}<AvgNote value={AVERAGES.verbNouns} /></span>
                            </div>
                        </div>

                        <p className="section-label">
                            {rb.grammar.mutationsSection}
                            <InfoTip text={rb.grammar.mutationsInfoTip} />
                        </p>
                        <div className="occurrence-list">
                            <div className="occurrence-row">
                                <span>{rb.grammar.mutations.total}</span>
                                <span>{r["Number of mutations"]}<AvgNote value={AVERAGES.totalMutations} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.mutations.soft}</span>
                                <span>{r["Number of Soft mutations"]}<AvgNote value={AVERAGES.softMutations} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.mutations.nasal}</span>
                                <span>{r["Number of Nasal mutations"]}<AvgNote value={AVERAGES.nasalMutations} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.mutations.aspirate}</span>
                                <span>{r["Number of Aspirate mutations"]}<AvgNote value={AVERAGES.aspirateMutations} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.grammar.mutations.hProthesis}</span>
                                <span>{r["Number of h-prothesis"]}<AvgNote value={AVERAGES.hProthesis} /></span>
                            </div>
                        </div>
                    </section>
                )
            }

            {/* ── Structure (was Syntax) ── */}
            {
                activeTab === "structure" && (
                    <section className="tab-section">
                        <p className="section-label">
                            {rb.structure.complexitySection}
                            <InfoTip text={rb.structure.complexityInfoTip} />
                        </p>
                        <div className="occurrence-list">
                            <div className="occurrence-row">
                                <span>
                                    {rb.structure.headDistance}
                                    <InfoTip text={rb.structure.headDistanceInfoTip} />                                </span>
                                <span>{fmt(r["Average head distance"])}<AvgNote value={AVERAGES.avgHeadDistance} /></span>
                            </div>

                            <div className="occurrence-row">
                                <span>
                                    {rb.structure.avgTreeDepth}
                                    <InfoTip text={rb.structure.avgTreeDepthInfoTip} />
                                </span>
                                <span>{fmt(r["Average depth"])}<AvgNote value={AVERAGES.avgTreeDepth} /></span>
                            </div>

                            <div className="occurrence-row">
                                <span>
                                    {rb.structure.maxTreeDepth}
                                    <InfoTip text={rb.structure.maxTreeDepthInfoTip} />
                                </span>
                                <span>{r["Max Tree depth"]}<AvgNote value={AVERAGES.maxTreeDepth} /></span>
                            </div>

                            <div className="occurrence-row">
                                <span>
                                    {rb.structure.branchingFactor}
                                    <InfoTip text={rb.structure.branchingFactorInfoTip} />
                                </span>
                                <span>{fmt(r["Max branching factor"])}<AvgNote value={AVERAGES.avgBranchingFactor} /></span>
                            </div>
                        </div>

                        <p className="section-label">{rb.structure.clauseSection}</p>
                        <div className="occurrence-list">
                            <div className="occurrence-row">
                                <span>{rb.structure.subjectRatio} <InfoTip text={rb.structure.subjectRatioInfoTip} /></span>
                                <span>{fmt(r["Subject ratio"])}<AvgNote value={AVERAGES.subjectRatio} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.structure.leftRightRatio} <InfoTip text={rb.structure.leftRightRatioInfoTip} /></span>
                                <span>{fmt(r["Left-right branching ratio"])}<AvgNote value={AVERAGES.leftRightBranchingRatio} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.structure.clausesPerSentence}</span>
                                <span>{fmt(r["Clause count per sentence"])}<AvgNote value={AVERAGES.clausesPerSentence} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.structure.totalClauses}</span>
                                <span>{r["Total clause count"]}<AvgNote value={AVERAGES.totalClauses} /></span>
                            </div>
                            <div className="occurrence-row">
                                <span>{rb.structure.auxiliaryVerbs} <InfoTip text={rb.structure.auxiliaryVerbsInfoTip} /></span>
                                <span>{r["Number of auxiliaries"]}<AvgNote value={AVERAGES.auxiliaryVerbs} /></span>
                            </div>
                        </div>
                    </section>
                )
            }

            {/* ── Suggestions ── */}
            {
                activeTab === "suggestions" && (
                    <section className="tab-section">
                        <p className="section-label">{rb.suggestions.section}</p>
                        <p className="section-label error">{rb.suggestions.placeholderNote}</p>
                        <div className="suggestion-grid">
                            {generateSuggestions(data, t).map((s, i) => (
                                <SuggestionCard key={i} title={s.title} body={s.body} colour={C[i]} />
                            ))}
                            {generateSuggestions(data, t).length === 0 && (
                                <p>{rb.suggestions.none}</p>
                            )}
                        </div>
                    </section>
                )
            }
        </article>
    );
}
